// src/js/cindy/lap-parser.ts
//
// (a) This is the SINGLE sanctioned Cindy note parser (P-18). Both `src/js/workouts.ts` (the
//     `/workouts/new` and `/workouts/<id>` entry/edit page) and `src/js/home/cindy-modal.ts` (the
//     sixth home-dashboard modal, 263-11) import `parseCindyNote()` from here — neither may
//     reimplement it. Two independently-built copies of this parsing logic is the exact failure
//     class `App\Utils\McPlayerPayload` was built to prevent (quick-task `260729-hy2`: a signal
//     and a template built the same payload independently and drifted).
// (b) Deliberately zero-import so `tools/probe-263-lap-parser.mjs` can import this module
//     directly under Node's native TypeScript type stripping — no browser, no bundler. This is
//     the same isolation rule `src/js/home/day-snap.ts` established in Phase 262.
// (c) The `-` terminator is load-bearing (P-02): the operator's real notes have a fixed
//     five-part structure (date header, laps, `-`, total, movement totals) and the lines after
//     the terminator are a SUMMARY, never additional laps. A naive "scan for time-shaped tokens"
//     parser reads the note's own total line as an extra lap, silently inflating a 9-round
//     session to 10 with numbers that still look plausible. When the terminator is missing
//     outright but a summary section still exists, this module falls back to the P-03b
//     movement-total checksum to infer where the laps end — and ALWAYS reports the inference as
//     an error so the caller can surface it, rather than silently trusting a guess.

/** Derived checksum figures for one parsed note (see 263-02-PLAN.md's `interface_context`). */
export interface CindyChecksumDetail {
    impliedRounds: number | null;
    impliedPartialReps: number | null;
    pull: number | null;
    push: number | null;
    squat: number | null;
}

/** Result of `parseCindyNote()` — the shape both consuming surfaces (263-05, 263-11) are written against. */
export interface ParsedCindyNote {
    date: string | null;
    rounds: string[];
    checksumOk: boolean | null;
    checksumDetail: CindyChecksumDetail | null;
    errors: string[];
}

type MovementKind = 'pull' | 'push' | 'squat';

const HEADER_RE = /^#*\s*(\d{4}-\d{2}-\d{2})\s*$/;
const LAP_PREFIX_RE = /^lap\s*\d+\s*:?\s*(.+)$/i;
const TIME_RE = /^\d{1,3}:\d{2}(?:\.\d{1,3})?$/;
const BARE_SECONDS_RE = /^\d+(?:\.\d+)?$/;
const MOVEMENT_RE = /^(\d+)\s+(pull|push|squat)(?:e?s)?\s*$/i;

const UNREADABLE_LINE_HINT = 'Expected a time like 2:11 or 02:11.34.';

/** P-03b's checksum tolerance for the terminator-omitted fallback (empirically <=0.06s across five real sessions). */
const CHECKSUM_TOLERANCE_SECONDS = 0.5;

function stripLapPrefix(text: string): string {
    const m = text.match(LAP_PREFIX_RE);
    return m ? m[1].trim() : text;
}

function isTimeShaped(text: string): boolean {
    return TIME_RE.test(text) || BARE_SECONDS_RE.test(text);
}

/** Convert an already-prefix-stripped `MM:SS.hh` / `M:SS` / bare-seconds string to seconds. */
function timeToSeconds(text: string): number {
    if (!text.includes(':')) {
        return Number(text);
    }
    const [mmss, hh] = text.split('.');
    const [mm, ss] = mmss.split(':');
    const hundredths = hh ? Number(hh) / Math.pow(10, hh.length) : 0;
    return Number(mm) * 60 + Number(ss) + hundredths;
}

type Classification =
    | { type: 'header'; lineNo: number; date: string }
    | { type: 'terminator'; lineNo: number }
    | { type: 'movement'; lineNo: number; kind: MovementKind; count: number }
    | { type: 'time'; lineNo: number; text: string }
    | { type: 'unrecognized'; lineNo: number; raw: string };

function classifyLine(rawLine: string, lineNo: number): Classification | null {
    const trimmed = rawLine.trim();
    if (trimmed === '') {
        return null;
    }
    const headerMatch = trimmed.match(HEADER_RE);
    if (headerMatch) {
        return { type: 'header', lineNo, date: headerMatch[1] };
    }
    if (trimmed === '-') {
        return { type: 'terminator', lineNo };
    }
    const movementMatch = trimmed.match(MOVEMENT_RE);
    if (movementMatch) {
        return {
            type: 'movement',
            lineNo,
            kind: movementMatch[2].toLowerCase() as MovementKind,
            count: Number(movementMatch[1]),
        };
    }
    const stripped = stripLapPrefix(trimmed);
    if (isTimeShaped(stripped)) {
        return { type: 'time', lineNo, text: stripped };
    }
    return { type: 'unrecognized', lineNo, raw: trimmed };
}

function firstCount(
    entries: { lineNo: number; kind: MovementKind; count: number }[],
    kind: MovementKind
): number | null {
    const found = entries.find((e) => e.kind === kind);
    return found ? found.count : null;
}

/**
 * Parse a pasted Cindy note (operator-typed, hand-transcribed from the iOS Clock stopwatch — see
 * P-01/P-02) into structured round data. Pure function: never throws, never fabricates a passing
 * checksum, never silently drops an unreadable line. Unit conversion (MM:SS.hh -> storage units)
 * is deliberately NOT this module's job — see header note (a).
 */
export function parseCindyNote(raw: string): ParsedCindyNote {
    const lines = raw.split('\n');

    let date: string | null = null;
    let terminatorLineNo: number | null = null;
    const timeEntries: { lineNo: number; text: string }[] = [];
    const movementEntries: { lineNo: number; kind: MovementKind; count: number }[] = [];
    const errors: string[] = [];

    lines.forEach((rawLine, idx) => {
        const lineNo = idx + 1;
        const c = classifyLine(rawLine, lineNo);
        if (c === null) {
            return;
        }
        switch (c.type) {
            case 'header':
                if (date === null) {
                    date = c.date;
                }
                break;
            case 'terminator':
                if (terminatorLineNo === null) {
                    terminatorLineNo = c.lineNo;
                }
                break;
            case 'movement':
                movementEntries.push({ lineNo: c.lineNo, kind: c.kind, count: c.count });
                break;
            case 'time':
                timeEntries.push({ lineNo: c.lineNo, text: c.text });
                break;
            case 'unrecognized':
                errors.push(`Couldn't read line ${c.lineNo}: "${c.raw}". ${UNREADABLE_LINE_HINT}`);
                break;
        }
    });

    let rounds: string[];

    if (terminatorLineNo !== null) {
        // Terminator present: everything before it is a lap, everything after it is summary —
        // NEVER treat a post-terminator line as a lap.
        const t: number = terminatorLineNo;
        rounds = timeEntries.filter((e) => e.lineNo < t).map((e) => e.text);
    } else if (movementEntries.length > 0) {
        // No terminator, but a summary section exists — this is P-02's exact corruption
        // scenario. Fall back to the P-03b checksum to infer where the laps end, and ALWAYS
        // report the missing terminator so the caller can surface it; never silently trust the
        // inference.
        const firstMovementLineNo = Math.min(...movementEntries.map((e) => e.lineNo));
        const candidates = timeEntries.filter((e) => e.lineNo < firstMovementLineNo);
        if (candidates.length >= 2) {
            const last = candidates[candidates.length - 1];
            const rest = candidates.slice(0, -1);
            const sumRest = rest.reduce((acc, e) => acc + timeToSeconds(e.text), 0);
            const lastSeconds = timeToSeconds(last.text);
            if (Math.abs(sumRest - lastSeconds) <= CHECKSUM_TOLERANCE_SECONDS) {
                rounds = rest.map((e) => e.text);
                errors.push(
                    "Missing '-' terminator line before the summary — inferred line " +
                    `${last.lineNo} ("${last.text}") is the session total (its value matches the ` +
                    `sum of the preceding ${rest.length} laps), not an extra round. Add the '-' ` +
                    'line to remove this warning.'
                );
            } else {
                rounds = candidates.map((e) => e.text);
                errors.push(
                    "Missing '-' terminator line before the summary — couldn't tell which line " +
                    'is the session total, so every time-shaped line up to the movement totals ' +
                    'was counted as a round. Verify the round count.'
                );
            }
        } else {
            rounds = candidates.map((e) => e.text);
            errors.push(
                "Missing '-' terminator line before the summary — couldn't tell which line is " +
                'the session total. Verify the round count.'
            );
        }
    } else {
        // No terminator and no summary section at all — nothing to disambiguate against, so
        // every time-shaped line is a round.
        rounds = timeEntries.map((e) => e.text);
    }

    const pull = firstCount(movementEntries, 'pull');
    const push = firstCount(movementEntries, 'push');
    const squat = firstCount(movementEntries, 'squat');

    let checksumOk: boolean | null = null;
    let checksumDetail: CindyChecksumDetail | null = null;

    if (pull !== null && push !== null && squat !== null) {
        // P-03b: pull = rounds x 5, push = rounds x 10, squat = rounds x 15 for a FULL round.
        // The number of fully-completed rounds is bounded by whichever movement is furthest
        // behind; the remainder (walked in Cindy's prescribed pull -> push -> squat order) is
        // the partial-round rep count.
        const impliedRounds = Math.min(Math.floor(pull / 5), Math.floor(push / 10), Math.floor(squat / 15));
        const leftoverPull = pull - impliedRounds * 5;
        const leftoverPush = push - impliedRounds * 10;
        const leftoverSquat = squat - impliedRounds * 15;
        const impliedPartialReps = leftoverPull + leftoverPush + leftoverSquat;
        checksumDetail = { impliedRounds, impliedPartialReps, pull, push, squat };
        checksumOk = impliedRounds === rounds.length;
    }

    return { date, rounds, checksumOk, checksumDetail, errors };
}
